const NEVER_STALE = Object.freeze({ isStale: () => false, message: '' });

/**
 * Sends an invoice from a dialog and disables the dialog while the request runs. A recorded invoice closes
 * it, is confirmed and dispatches `billing-changed` on `document`. A rejection is shown once: inside the
 * open dialog, so the input can be corrected, else in a toast. An expired session always goes to the toast,
 * since the app reloads to the login screen right after; the dialog closes first, as its backdrop would
 * cover the toast.
 *
 * @param {{ submitting: boolean, close: () => void, showError: (message: string) => boolean }} dialog
 * @param {{ show: (message: string, type: string) => void }} notification
 * @param {() => void} reloadPage
 * @param {() => Promise<unknown>} send
 * @param {{ isStale: (error: Error & { status?: number }) => boolean, message: string }} [stale] a rejection
 *   because the data changed since the dialog opened: shows `message` and dispatches `billing-changed`, so the
 *   page behind the dialog reloads
 */
export async function submitInvoice(dialog, notification, reloadPage, send, stale = NEVER_STALE) {
  dialog.submitting = true;
  try {
    await send();
    dialog.close();
    notification.show('Invoice recorded', 'success');
    document.dispatchEvent(new CustomEvent('billing-changed'));
  } catch (error) {
    if (error.status === 401) {
      dialog.close();
      notification.show('Session expired. Please login again.', 'error');
      setTimeout(() => reloadPage(), 2000);
    } else {
      const isStale = stale.isStale(error);
      const message = isStale ? stale.message : `Could not record the invoice: ${error.message}`;
      if (!dialog.showError(message)) notification.show(message, 'error');
      if (isStale) document.dispatchEvent(new CustomEvent('billing-changed'));
    }
  } finally {
    dialog.submitting = false;
  }
}
